import { get, isEmpty, set, unset } from 'lodash'
import { INPUT_KEYS } from '../policy_feature/inputs'

export const hasDisplayableFormValue = (value) => {
  if (typeof value === 'boolean' || typeof value === 'number') {
    return true
  }

  return !isEmpty(value)
}

const isBlank = (value) => value === undefined || value === null || value === ''

const FILTER_PATHS = [
  'conditions.applications.applicationFilter',
  'conditions.devices.deviceFilter',
  'conditions.clientApplications.servicePrincipalFilter',
]

const SESSION_OBJECT_PATHS = [
  'sessionControls.signInFrequency',
  'sessionControls.persistentBrowser',
  'sessionControls.applicationEnforcedRestrictions',
  'sessionControls.cloudAppSecurity',
  'sessionControls.continuousAccessEvaluation',
  'sessionControls.secureSignInSession',
  'sessionControls.globalSecureAccessFilteringProfile',
]

const hasFilterValues = (filter) => Boolean(filter?.mode || filter?.rule)

const hasSignInFrequencyValues = (signInFrequency) =>
  signInFrequency?.isEnabled === true ||
  signInFrequency?.isEnabled === false ||
  signInFrequency?.value != null ||
  Boolean(signInFrequency?.type) ||
  Boolean(signInFrequency?.frequencyInterval) ||
  Boolean(signInFrequency?.authenticationType)

const hasPersistentBrowserValues = (persistentBrowser) =>
  persistentBrowser?.isEnabled === true || persistentBrowser?.isEnabled === false || Boolean(persistentBrowser?.mode)

const hasCloudAppSecurityValues = (cloudAppSecurity) =>
  cloudAppSecurity?.isEnabled === true ||
  cloudAppSecurity?.isEnabled === false ||
  Boolean(cloudAppSecurity?.cloudAppSecurityType)

const hasApplicationEnforcedRestrictionsValues = (applicationEnforcedRestrictions) =>
  applicationEnforcedRestrictions?.isEnabled === true || applicationEnforcedRestrictions?.isEnabled === false

const hasContinuousAccessEvaluationValues = (continuousAccessEvaluation) => Boolean(continuousAccessEvaluation?.mode)

const hasSecureSignInSessionValues = (secureSignInSession) =>
  secureSignInSession?.isEnabled === true || secureSignInSession?.isEnabled === false

const hasGlobalSecureAccessFilteringProfileValues = (globalSecureAccessFilteringProfile) =>
  globalSecureAccessFilteringProfile?.isEnabled === true ||
  globalSecureAccessFilteringProfile?.isEnabled === false ||
  Boolean(globalSecureAccessFilteringProfile?.profileId)

const pruneEmptyFilter = (policy, path) => {
  const filter = get(policy, path)
  if (!filter || hasFilterValues(filter)) {
    return
  }

  set(policy, path, null)
}

const pruneEmptySessionObjects = (policy) => {
  const signInFrequency = get(policy, 'sessionControls.signInFrequency')
  if (signInFrequency && !hasSignInFrequencyValues(signInFrequency)) {
    set(policy, 'sessionControls.signInFrequency', null)
  }

  const persistentBrowser = get(policy, 'sessionControls.persistentBrowser')
  if (persistentBrowser && !hasPersistentBrowserValues(persistentBrowser)) {
    set(policy, 'sessionControls.persistentBrowser', null)
  }

  const cloudAppSecurity = get(policy, 'sessionControls.cloudAppSecurity')
  if (cloudAppSecurity && !hasCloudAppSecurityValues(cloudAppSecurity)) {
    set(policy, 'sessionControls.cloudAppSecurity', null)
  }

  const applicationEnforcedRestrictions = get(policy, 'sessionControls.applicationEnforcedRestrictions')
  if (applicationEnforcedRestrictions && !hasApplicationEnforcedRestrictionsValues(applicationEnforcedRestrictions)) {
    set(policy, 'sessionControls.applicationEnforcedRestrictions', null)
  }

  const continuousAccessEvaluation = get(policy, 'sessionControls.continuousAccessEvaluation')
  if (continuousAccessEvaluation && !hasContinuousAccessEvaluationValues(continuousAccessEvaluation)) {
    set(policy, 'sessionControls.continuousAccessEvaluation', null)
  }

  const secureSignInSession = get(policy, 'sessionControls.secureSignInSession')
  if (secureSignInSession && !hasSecureSignInSessionValues(secureSignInSession)) {
    set(policy, 'sessionControls.secureSignInSession', null)
  }

  const globalSecureAccessFilteringProfile = get(policy, 'sessionControls.globalSecureAccessFilteringProfile')
  if (
    globalSecureAccessFilteringProfile &&
    !hasGlobalSecureAccessFilteringProfileValues(globalSecureAccessFilteringProfile)
  ) {
    set(policy, 'sessionControls.globalSecureAccessFilteringProfile', null)
  }
}

const hasSessionControlValues = (sessionControls) => {
  if (!sessionControls) {
    return false
  }

  return (
    hasSignInFrequencyValues(sessionControls.signInFrequency) ||
    hasPersistentBrowserValues(sessionControls.persistentBrowser) ||
    hasCloudAppSecurityValues(sessionControls.cloudAppSecurity) ||
    hasApplicationEnforcedRestrictionsValues(sessionControls.applicationEnforcedRestrictions) ||
    hasContinuousAccessEvaluationValues(sessionControls.continuousAccessEvaluation) ||
    hasSecureSignInSessionValues(sessionControls.secureSignInSession) ||
    hasGlobalSecureAccessFilteringProfileValues(sessionControls.globalSecureAccessFilteringProfile) ||
    sessionControls.disableResilienceDefaults === true ||
    sessionControls.disableResilienceDefaults === false
  )
}

const pruneEmptyClientApplications = (policy) => {
  const clientApplications = policy?.conditions?.clientApplications
  if (!clientApplications) {
    return
  }

  const hasValues =
    clientApplications.includeServicePrincipals?.length ||
    clientApplications.excludeServicePrincipals?.length ||
    hasFilterValues(clientApplications.servicePrincipalFilter)

  if (!hasValues) {
    set(policy, 'conditions.clientApplications', null)
  }
}

const pruneEmptyDevices = (policy) => {
  const devices = policy?.conditions?.devices
  if (!devices) {
    return
  }

  const hasValues =
    devices.includeDevices?.length || devices.excludeDevices?.length || hasFilterValues(devices.deviceFilter)

  if (!hasValues) {
    set(policy, 'conditions.devices', null)
  }
}

export const pruneEmptyMappedPolicyObjects = (policy, { removeEmptyValues = true } = {}) => {
  FILTER_PATHS.forEach((path) => pruneEmptyFilter(policy, path))
  pruneEmptySessionObjects(policy)
  pruneEmptyClientApplications(policy)
  pruneEmptyDevices(policy)

  if (!removeEmptyValues) {
    return
  }

  FILTER_PATHS.forEach((path) => {
    if (get(policy, path) === null) {
      unset(policy, path)
    }
  })

  SESSION_OBJECT_PATHS.forEach((path) => {
    if (get(policy, path) === null) {
      unset(policy, path)
    }
  })

  if (policy?.sessionControls && !hasSessionControlValues(policy.sessionControls)) {
    unset(policy, 'sessionControls')
  } else if (policy?.sessionControls) {
    SESSION_OBJECT_PATHS.forEach((path) => {
      if (get(policy, path) === undefined) {
        unset(policy, path)
      }
    })

    if (isBlank(policy.sessionControls.disableResilienceDefaults)) {
      unset(policy, 'sessionControls.disableResilienceDefaults')
    }
  }

  if (policy?.conditions?.clientApplications === null) {
    unset(policy, 'conditions.clientApplications')
  }

  if (policy?.conditions?.devices === null) {
    unset(policy, 'conditions.devices')
  }
}

export const collectUniquePolicyPathValues = (policyConfigurations, path) => {
  const uniqueValues = new Set()

  policyConfigurations?.data?.forEach((item) => {
    const rawValue = get(item?.rawPolicy, path)
    let values = []
    if (Array.isArray(rawValue)) {
      values = rawValue
    } else if (rawValue) {
      values = [rawValue]
    }
    values.forEach((value) => {
      if (value) {
        uniqueValues.add(value)
      }
    })
  })

  return [...uniqueValues].map((value) => ({ label: value, value }))
}

export const TENANT_SPECIFIC_ID_INPUTS = new Set([
  INPUT_KEYS.termsOfUse,
  INPUT_KEYS.includeAuthenticationContextClassReferences,
  INPUT_KEYS.includeServicePrincipals,
  INPUT_KEYS.excludeServicePrincipals,
  INPUT_KEYS.globalSecureAccessFilteringProfileId,
])

export const FORM_KEYS_SKIP_OPTION_VALIDATION = new Set([
  INPUT_KEYS.applicationFilterRule,
  INPUT_KEYS.deviceFilterRule,
  INPUT_KEYS.servicePrincipalFilterRule,
  INPUT_KEYS.signInFrequencyIsEnabled,
  INPUT_KEYS.signInFrequencyValue,
  INPUT_KEYS.persistentBrowserIsEnabled,
  INPUT_KEYS.applicationEnforcedRestrictionsIsEnabled,
  INPUT_KEYS.cloudAppSecurityIsEnabled,
  INPUT_KEYS.disableResilienceDefaults,
  INPUT_KEYS.secureSignInSessionIsEnabled,
  INPUT_KEYS.globalSecureAccessFilteringProfileIsEnabled,
  INPUT_KEYS.globalSecureAccessFilteringProfileId,
  INPUT_KEYS.termsOfUse,
  INPUT_KEYS.includeAuthenticationContextClassReferences,
  INPUT_KEYS.includeServicePrincipals,
  INPUT_KEYS.excludeServicePrincipals,
  INPUT_KEYS.includeDevices,
  INPUT_KEYS.excludeDevices,
])
